import React, { useState } from 'react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  CreditCard,
  Users,
  Star
} from 'lucide-react';

const RevenueAnalytics = () => {
  const { analytics, exportAnalytics } = useAnalytics();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  const revenue = analytics.revenue || {};
  const monthlyRevenue = revenue.monthly || [];
  const revenueByCourse = revenue.byCourse || [];
  const revenueBySubscription = revenue.bySubscription || [];

  const handleExport = () => {
    const result = exportAnalytics('csv');
    if (result.success) {
      alert('Données financières exportées avec succès !');
    } else {
      alert('Erreur lors de l\'export');
    }
  };

  const getTotalRevenue = () => {
    return monthlyRevenue.reduce((sum, month) => sum + month.revenue, 0);
  };

  const getRevenueGrowth = () => {
    if (monthlyRevenue.length < 2) return 0;
    const current = monthlyRevenue[monthlyRevenue.length - 1].revenue;
    const previous = monthlyRevenue[monthlyRevenue.length - 2].revenue;
    return ((current - previous) / previous) * 100;
  };

  const getAverageRevenuePerStudent = () => {
    const totalStudents = monthlyRevenue.reduce((sum, month) => sum + month.students, 0);
    const totalRevenue = getTotalRevenue();
    return totalStudents > 0 ? totalRevenue / totalStudents : 0;
  };

  return (
    <div className="space-y-6">
      {/* Header avec contrôles */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics des Revenus</h2>
          <p className="text-gray-600">Analyse financière détaillée de la plateforme</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="revenue">Revenus</option>
            <option value="students">Étudiants</option>
            <option value="growth">Croissance</option>
          </select>
          
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
        </div>
      </div>

      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Revenus Totaux</p>
              <p className="text-3xl font-bold text-gray-900">€{getTotalRevenue().toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-1">
                {getRevenueGrowth() >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )}
                <span className={`text-sm ${getRevenueGrowth() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(getRevenueGrowth()).toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Revenus ce Mois</p>
              <p className="text-3xl font-bold text-gray-900">
                €{analytics.overview?.revenueThisMonth?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-gray-500">Janvier 2024</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Revenus par Étudiant</p>
              <p className="text-3xl font-bold text-gray-900">
                €{getAverageRevenuePerStudent().toFixed(0)}
              </p>
              <p className="text-sm text-gray-500">Moyenne</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Taux de Conversion</p>
              <p className="text-3xl font-bold text-gray-900">34.7%</p>
              <p className="text-sm text-green-600">+2.3% vs mois dernier</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Revenus mensuels */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Revenus</h3>
        <div className="space-y-4">
          {monthlyRevenue.map((month, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {new Date(month.month + ' 1, 2024').toLocaleDateString('fr-FR', { month: 'short' })}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{month.month}</h4>
                  <p className="text-sm text-gray-600">{month.students} nouveaux étudiants</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">€{month.revenue.toLocaleString()}</p>
                <p className="text-sm text-gray-600">
                  €{Math.round(month.revenue / month.students).toLocaleString()} par étudiant
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Revenus par cours et abonnement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenus par Cours</h3>
          <div className="space-y-4">
            {revenueByCourse.map((course, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                  <span className="text-sm text-gray-900 truncate">{course.course}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${course.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-16 text-right">
                    €{course.revenue.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenus par Abonnement</h3>
          <div className="space-y-4">
            {revenueBySubscription.map((subscription, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{subscription.plan}</h4>
                    <p className="text-sm text-gray-600">{subscription.users} utilisateurs</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">€{subscription.revenue.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">
                    €{subscription.users > 0 ? Math.round(subscription.revenue / subscription.users) : 0}/utilisateur
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Analyse des tendances */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Analyse des Tendances</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Croissance Positive</h4>
            <p className="text-sm text-gray-600">
              Les revenus augmentent de {getRevenueGrowth().toFixed(1)}% par rapport au mois dernier
            </p>
          </div>

          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Acquisition</h4>
            <p className="text-sm text-gray-600">
              {monthlyRevenue[monthlyRevenue.length - 1]?.students || 0} nouveaux étudiants ce mois
            </p>
          </div>

          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Valeur Client</h4>
            <p className="text-sm text-gray-600">
              Valeur moyenne par étudiant : €{getAverageRevenuePerStudent().toFixed(0)}
            </p>
          </div>
        </div>
      </div>

      {/* Projections */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Projections</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Revenus Prévus (Mois Prochain)</h4>
            <p className="text-2xl font-bold text-primary">
              €{Math.round((analytics.overview?.revenueThisMonth || 0) * (1 + getRevenueGrowth() / 100)).toLocaleString()}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Basé sur la tendance actuelle de {getRevenueGrowth().toFixed(1)}%
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Objectif Annuel</h4>
            <p className="text-2xl font-bold text-green-600">
              €{Math.round(getTotalRevenue() * 12).toLocaleString()}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Projection basée sur les 4 derniers mois
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalytics;
